import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { XMarkIcon, PhotoIcon, TagIcon } from '@heroicons/react/24/outline'
import ImageUpload from '../Common/ImageUpload'
import MultipleImageUpload from '../Common/MultipleImageUpload'

const BlogForm = ({ blog, onSubmit, onClose, isLoading, isModal = true }) => {
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        tags: [],
        featured_image: '',
        gallery_images: [],
        featured: false,
        status: 'draft',
        meta_title: '',
        meta_description: '',
        meta_keywords: []
    })

    const [tagInput, setTagInput] = useState('')
    const [keywordInput, setKeywordInput] = useState('')

    useEffect(() => {
        if (blog) {
            setFormData({
                title: blog.title || '',
                excerpt: blog.excerpt || '',
                content: blog.content || '',
                category: blog.category || '',
                tags: blog.tags || [],
                featured_image: blog.featured_image || '',
                gallery_images: blog.gallery_images || [],
                featured: blog.featured || false,
                status: blog.status || 'draft',
                meta_title: blog.meta_title || '',
                meta_description: blog.meta_description || '',
                meta_keywords: blog.meta_keywords || []
            })
        } else {
            // Reset form for new blog
            setFormData({
                title: '',
                excerpt: '',
                content: '',
                category: '',
                tags: [],
                featured_image: '',
                gallery_images: [],
                featured: false,
                status: 'draft',
                meta_title: '',
                meta_description: '',
                meta_keywords: []
            })
        }
    }, [blog])

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tagInput.trim()]
            }))
            setTagInput('')
        }
    }

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }))
    }

    const handleAddKeyword = () => {
        if (keywordInput.trim() && !formData.meta_keywords.includes(keywordInput.trim())) {
            setFormData(prev => ({
                ...prev,
                meta_keywords: [...prev.meta_keywords, keywordInput.trim()]
            }))
            setKeywordInput('')
        }
    }

    const handleRemoveKeyword = (keywordToRemove) => {
        setFormData(prev => ({
            ...prev,
            meta_keywords: prev.meta_keywords.filter(keyword => keyword !== keywordToRemove)
        }))
    }

    const handleKeyPress = (e, action) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            action()
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        // Generate slug from title if not editing or if title changed
        const submitData = { ...formData }
        if (!blog || (blog && blog.title !== formData.title)) {
            submitData.slug = formData.title
                .toLowerCase()
                .replace(/[^a-z0-9 -]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .trim('-')
        }

        onSubmit(submitData)
    }

    const categories = [
        'Education News',
        'Career Guidance',
        'Study Tips',
        'University Updates',
        'Admission Process',
        'Scholarships',
        'Exam Preparation',
        'Industry Insights',
        'Student Success Stories',
        'Technology in Education',
        'Online Learning',
        'International Education',
        'Skill Development',
        'Job Market Trends',
        'Higher Education',
        'Research Opportunities',
        'Campus Life',
        'Alumni Stories',
        'Education Policy',
        'Learning Resources',
        'Other'
    ]

    const formContent = (
        <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900">
                        {blog ? 'Edit Blog' : 'Create New Blog'}
                    </h3>
                    {isModal && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Enter blog title"
                            />
                        </div>

                        {/* Excerpt */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Excerpt *
                            </label>
                            <textarea
                                name="excerpt"
                                value={formData.excerpt}
                                onChange={handleInputChange}
                                required
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Brief description of the blog post"
                            />
                        </div>

                        {/* Content */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Content *
                            </label>
                            <textarea
                                name="content"
                                value={formData.content}
                                onChange={handleInputChange}
                                required
                                rows={15}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Write your blog content here. You can use HTML tags for formatting."
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                You can use HTML tags for formatting. For example: &lt;p&gt;, &lt;h1&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;a&gt;, &lt;img&gt;
                            </p>
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Tags
                            </label>
                            <div className="flex space-x-2 mb-2">
                                <input
                                    type="text"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyPress={(e) => handleKeyPress(e, handleAddTag)}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="Add a tag and press Enter"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddTag}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                >
                                    Add
                                </button>
                            </div>
                            {formData.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {formData.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                className="ml-2 text-blue-600 hover:text-blue-800"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Category */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category *
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>

                        {/* Featured */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="featured"
                                checked={formData.featured}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900">
                                Featured Blog
                            </label>
                        </div>

                        {/* Featured Image */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Featured Image
                            </label>
                            <ImageUpload
                                value={formData.featured_image}
                                onChange={(imageUrl) => setFormData(prev => ({ ...prev, featured_image: imageUrl }))}
                                className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg"
                            />
                        </div>

                        {/* Gallery Images */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Gallery Images
                            </label>
                            <MultipleImageUpload
                                images={formData.gallery_images.map(img => typeof img === 'string' ? { url: img, caption: '', type: 'image' } : img)}
                                onChange={(images) => setFormData(prev => ({ ...prev, gallery_images: images.map(img => img.url) }))}
                                maxImages={10}
                            />
                        </div>

                        {/* SEO Section */}
                        <div className="border-t pt-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-4">SEO Settings</h4>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Meta Title
                                    </label>
                                    <input
                                        type="text"
                                        name="meta_title"
                                        value={formData.meta_title}
                                        onChange={handleInputChange}
                                        maxLength={60}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="SEO title (max 60 characters)"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        {formData.meta_title.length}/60 characters
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Meta Description
                                    </label>
                                    <textarea
                                        name="meta_description"
                                        value={formData.meta_description}
                                        onChange={handleInputChange}
                                        maxLength={160}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="SEO description (max 160 characters)"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        {formData.meta_description.length}/160 characters
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Meta Keywords
                                    </label>
                                    <div className="flex space-x-2 mb-2">
                                        <input
                                            type="text"
                                            value={keywordInput}
                                            onChange={(e) => setKeywordInput(e.target.value)}
                                            onKeyPress={(e) => handleKeyPress(e, handleAddKeyword)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="Add keyword and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddKeyword}
                                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    {formData.meta_keywords.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.meta_keywords.map((keyword, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800"
                                                >
                                                    {keyword}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveKeyword(keyword)}
                                                        className="ml-1 text-gray-600 hover:text-gray-800"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? 'Saving...' : (blog ? 'Update Blog' : 'Create Blog')}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                    Cancel
                </button>
            </div>
        </form>
    )

    if (isModal) {
        return (
            <AnimatePresence>
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            onClick={onClose}
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
                        >
                            {formContent}
                        </motion.div>
                    </div>
                </div>
            </AnimatePresence>
        )
    }

    return formContent
}

export default BlogForm